import { Fragment, ReactNode } from 'react';
import { useTranslation as useTranslationStatic } from '@qlub-dev/next-export-i18n';
import { omit } from 'lodash';
import { extractDigitsIsolate } from '../utility/localize';
import { useQlubRouter } from './router';

export const useTranslation = (ns: string = 'common') => {
    const { t: baseT } = useTranslationStatic(ns);
    const { url } = useQlubRouter();

    const t = (key: string, params?: Record<string, any>) => {
        try {
            if (params?.useLocale) {
                const countryCode = url.cc;
                const localeKey = `___locale___.${countryCode}.${key}`;
                const localizedValue = baseT(localeKey, omit(params, ['useLocale']));

                return localizedValue === localeKey ? baseT(key, params) : localizedValue;
            }

            return baseT(key, params);
        } catch {
            return baseT(key, params);
        }
    };

    return { t };
};

const name = 'Participant';

export const useParticipationTranslation = () => {
    const { t } = useTranslation('common');

    const trim = (val: string) => {
        if (val.indexOf(name) === 0) {
            const idx = extractDigitsIsolate(val.split(`${name} `)?.[1] || '1');
            return t('Participant {{idx}}', { idx });
        }
        return val;
    };

    return {
        trim,
    };
};

interface IPart {
    type: 'text' | 'format';
    text: string;
}

export const interpolateT = (
    str: string,
    params: { [key: string]: string | JSX.Element },
): Array<string | ReactNode> => {
    const found: IPart[] = [];
    const rxp = /<([^>]+)>/g;
    let curMatch: RegExpExecArray | null = null;
    let lastLength = 0;

    // eslint-disable-next-line no-cond-assign
    while ((curMatch = rxp.exec(str))) {
        if (curMatch.index !== 0 && found.length === 0) {
            found.push({
                type: 'text',
                text: str.substring(0, curMatch.index),
            });
        } else if (lastLength !== str.length && lastLength !== curMatch.index) {
            found.push({
                type: 'text',
                text: str.substring(lastLength, curMatch.index),
            });
        }
        found.push({
            type: 'format',
            text: curMatch[1],
        });
        lastLength = curMatch.index + curMatch[0].length;
    }

    if (lastLength !== str.length) {
        found.push({
            type: 'text',
            text: str.substring(lastLength),
        });
    }

    return found.map((item, i) => {
        if (item.type === 'text') {
            return <Fragment key={item.text}>{item.text}</Fragment>;
        }
        return <Fragment key={item.text || i}>{params[item.text] || item.text}</Fragment>;
    });
};
